import { useState } from "react";
import {
  ResponsiveContainer,
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";
import { TrendingUp } from "lucide-react";
import { MONTHLY_SERIES, formatCompactBRL, formatBRL } from "@/data/financeData";

const TooltipBox = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-lg">
      <p className="mb-1.5 text-xs font-semibold text-slate-500">{label}/2026</p>
      {payload.map((p) => (
        <p key={p.dataKey} className="flex items-center gap-2 text-xs">
          <span className="h-2 w-2 rounded-full" style={{ background: p.color }} />
          <span className="text-slate-600">
            {p.dataKey === "faturamento" ? "Faturamento" : "Lucro"}:
          </span>
          <span className="font-mono-num font-bold text-slate-900">{formatBRL(p.value)}</span>
        </p>
      ))}
    </div>
  );
};

export const RevenueChart = () => {
  const [metric, setMetric] = useState("ambos");

  return (
    <div className="rounded-2xl border border-slate-200/70 bg-white p-5 shadow-[0_2px_8px_rgba(0,0,0,0.04)] md:p-6">
      <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-50">
            <TrendingUp className="h-4 w-4 text-blue-600" />
          </div>
          <div>
            <h2 className="font-heading text-base font-bold text-slate-900">
              Faturamento & Lucro
            </h2>
            <p className="text-xs text-slate-500">Tendência dos últimos 6 meses</p>
          </div>
        </div>
        <div className="flex items-center gap-1 rounded-lg border border-slate-200 bg-slate-50 p-1">
          {[
            { id: "ambos", label: "Ambos" },
            { id: "faturamento", label: "Faturamento" },
            { id: "lucro", label: "Lucro" },
          ].map((m) => (
            <button
              key={m.id}
              data-testid={`chart-metric-${m.id}`}
              onClick={() => setMetric(m.id)}
              className={`rounded-md px-2.5 py-1 text-xs font-semibold transition-colors duration-200 ${
                metric === m.id ? "bg-white text-blue-700 shadow-sm" : "text-slate-500 hover:text-slate-800"
              }`}
            >
              {m.label}
            </button>
          ))}
        </div>
      </div>

      <div className="h-[280px] w-full" data-testid="revenue-chart">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={MONTHLY_SERIES} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
            <defs>
              <linearGradient id="barFat" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.9} />
                <stop offset="100%" stopColor="#3b82f6" stopOpacity={0.55} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
            <XAxis
              dataKey="mes"
              tick={{ fontSize: 12, fill: "#94a3b8" }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              tickFormatter={(v) => formatCompactBRL(v).replace("R$ ", "")}
              tick={{ fontSize: 11, fill: "#94a3b8" }}
              axisLine={false}
              tickLine={false}
              width={56}
            />
            <Tooltip content={<TooltipBox />} cursor={{ fill: "rgba(148,163,184,0.08)" }} />
            {(metric === "ambos" || metric === "faturamento") && (
              <Bar dataKey="faturamento" fill="url(#barFat)" radius={[6, 6, 0, 0]} barSize={30} />
            )}
            {(metric === "ambos" || metric === "lucro") && (
              <Line
                type="monotone"
                dataKey="lucro"
                stroke="#10b981"
                strokeWidth={3}
                dot={{ r: 4, fill: "#10b981", strokeWidth: 0 }}
                activeDot={{ r: 6 }}
              />
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      <div className="mt-3 flex items-center justify-center gap-5 text-xs">
        <span className="flex items-center gap-1.5 text-slate-500">
          <span className="h-2.5 w-2.5 rounded-sm bg-blue-500" /> Faturamento
        </span>
        <span className="flex items-center gap-1.5 text-slate-500">
          <span className="h-2.5 w-2.5 rounded-full bg-emerald-500" /> Lucro Líquido
        </span>
      </div>
    </div>
  );
};
